import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import ThreadList from '@components/threadList';
import UserMessageList from '@components/userMessageList';
import SystemList from '@components/systemList';
import MessageLikeAndFavoriteList from '@components/messageLikeAndFavoriteList';
import DownloadApp from '@components/downloadApp';
import SidebarTopicList from '@components/sidebarTopicList';
import SidebarUserInfo from '@components/sidebarUserInfo';
import { copy } from '@utils/copy';
import { clearAllCookies, clearStorages } from '@utils/cookie';
import Avatar from '@components/avatar';
import { BASE_IMG_URL } from '@images/imageUrls';
import Toast from '@components/Toast';
import TabNav  from '@components/tabNav';
import classNames from 'classnames';
import parse, { domToReact } from 'html-react-parser';
import { formatTime } from '@utils/timeUtils';
import EmojiPicker from '@components/EmojiPicker/pc';
import { Image, Spin } from 'antd';
import { getEmojiDisplayValue, isEmojiImageUrl } from '@utils/emoji';

interface RouteParams {
    roomId?: string;
}

interface PrivateMessagePCPageProps extends BasePageProps, RouteComponentProps<RouteParams> {
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    userInfo?: any
    unreadNotificationsData?: any
    handleLoadMore: any
    userChatList?: any[]
    privateUserInfo?: any   // URL usercode 对应的用户信息
    submitNotificationsReadData?: () => Promise<void>
    onSelectChatRoom?: (roomId: number) => void
    selectedRoomId?: number
    chatScrollTrigger?: number  // 递增时触发聊天区滚动到底部
    // 聊天输入相关 props（由父组件管理）
    chatInputContent?: string
    chatEmojiPickerVisible?: boolean
    chatUploading?: boolean
    chatSendLoading?: boolean
    onChatInputChange?: (content: string) => void
    onChatEmojiToggle?: () => void
    onChatEmojiClose?: () => void
    onChatFileUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onChatSend?: () => void
    // friendList?: any // 关注/粉丝列表
    // friendType?: any    // 关注、粉丝类型
}
interface PrivateMessagePCState { 
    loading: any
    error: any
    pageLoaded: any
    topicList?: any
    
}
@inject('app', 'site', 'user', 'thread')
@observer
class PrivateMessagePCPage extends BasePage<PrivateMessagePCPageProps, PrivateMessagePCState> {
    private fileInputRef = React.createRef<HTMLInputElement>();
    private textareaRef = React.createRef<HTMLTextAreaElement>();
    private emojiMapCache: Map<string, { value: string; isImage: boolean; category?: string }> | null = null;
    private emojiListStamp: any = null;
    private chatBoxRef = React.createRef<HTMLDivElement>();
    private prevChatScrollTrigger: number = 0;
    private prevScrollHeight: number = 0;       // 加载更多前记录 scrollHeight，用于恢复位置
    private isLoadingMore: boolean = false;      // 防止重复触发加载更多
    private prevUserChatLength: number = 0;      // 记录上一次 userChatList 长度
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        topicList: [],
    };
    componentDidMount() {
        const { app, user, thread } = this.props;
        super.componentDidMount();
    }

    componentDidUpdate() {
        const { chatScrollTrigger, userChatList } = this.props;
        const box = this.chatBoxRef.current;
        if (!box) return;

        const currentLength = userChatList?.length || 0;

        if (chatScrollTrigger !== this.prevChatScrollTrigger) {
            // 首次加载 / 切换房间：滚动到底部
            box.scrollTop = box.scrollHeight;
            this.prevChatScrollTrigger = chatScrollTrigger ?? 0;
        } else if (currentLength > this.prevUserChatLength && this.prevScrollHeight > 0) {
            // 加载更多历史数据（数据 prepend 到顶部）：恢复滚动位置
            box.scrollTop = box.scrollHeight - this.prevScrollHeight;
            this.prevScrollHeight = 0;
            this.isLoadingMore = false;
        }

        this.prevUserChatLength = currentLength;
    }

    // 聊天区滚动事件：触顶时加载更多历史消息
    handleChatScroll = () => {
        const box = this.chatBoxRef.current;
        if (!box || this.isLoadingMore) return;

        if (box.scrollTop <= 0) {
            this.isLoadingMore = true;
            this.prevScrollHeight = box.scrollHeight;
            this.props.handleLoadMore?.();
        }
    }

    // 表情选择：读取 textarea 光标位置，计算新内容后通知父组件
    handleEmojiSelect = (emojiValue: string, emojiCode?: string) => {
        // 图片表情发送 code，原生 Unicode emoji 直接发送字符，避免把字符当图片地址。
        const value = isEmojiImageUrl(emojiValue) ? (emojiCode || emojiValue) : (emojiValue || emojiCode || '');
        const textarea = this.textareaRef.current;
        const cursorPos = textarea?.selectionStart ?? 0;
        const { chatInputContent = '' } = this.props;
        const newContent = chatInputContent.slice(0, cursorPos) + value + chatInputContent.slice(cursorPos);

        this.props.onChatInputChange?.(newContent);
        this.props.onChatEmojiClose?.();

        // 恢复光标位置
        setTimeout(() => {
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(cursorPos + value.length, cursorPos + value.length);
            }
        }, 0);
    }

    // 处理文件选择，转发给父组件（上传后直接发送图片消息）
    handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.onChatFileUpload?.(e);
    }

    // 处理文本输入变化
    handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.props.onChatInputChange?.(e.target.value);
    }

    // 跳转到帖子详情页
    // handleThreadClick = (id: string | number) => {
    //     this.props.history && this.props.history.push(`/threadDeatil/${id}`);
    // };

    // 点击复制
    onCopyClick = (data) => {
        copy(data);
        Toast.success({ content: '已复制社区号' });
    }

    private escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    private getEmojiMap = () => {
        const emojiList = this.props.site?.emojiList || [];
        if (this.emojiListStamp === emojiList && this.emojiMapCache) return this.emojiMapCache;

        const map = new Map<string, { value: string; isImage: boolean; category?: string }>();
        emojiList.forEach((cat: any) => {
            (cat?.emoji || []).forEach((emoji: any) => {
                const value = getEmojiDisplayValue(emoji);
                if (emoji?.code && value) {
                    map.set(emoji.code, {
                        value,
                        isImage: isEmojiImageUrl(value),
                        category: cat?.category,
                    });
                }
            });
        });
        this.emojiMapCache = map;
        this.emojiListStamp = emojiList;
        return map;
    };

    private replaceEmojiInContent = (content?: string, useSmall: boolean = false) => {
        if (!content) return content || '';
        const map = this.getEmojiMap();
        if (!map || map.size === 0) return content;
        let result = content;
        map.forEach((info, code) => {
            const reg = new RegExp(this.escapeRegex(code), 'g');
            if (info.isImage) {
                // useSmall=true 时用小表情（评论区），false时根据category判断（正文）
                const cls = useSmall ? styles.emojiInlineSmall : (info.category === '默认' ? styles.emojiInlineSmall : styles.emojiInlineLarge);
                const imgTag = `<img src="${info.value}" alt="${code}" class="${cls}" />`;
                result = result.replace(reg, () => imgTag);
            } else {
                result = result.replace(reg, () => info.value);
            }
        });
        return result;
    };

    private getChatPeer = (item: any) => {
        const myCode = this.props.user?.userInfo?.code;
        if (!item) return null;
        if (item.receiver?.code && item.receiver.code !== myCode) return item.receiver;
        if (item.sender?.code && item.sender.code !== myCode) return item.sender;
        return item.receiver || item.sender || null;
    };

    // 私信列表
    renderChatList = () => {
        const { user, handleLoadMore, unreadNotificationsData } = this.props;
        if (!user || !user.listStore) {
            return null;
        }
        const list = user.listStore.lists['chatList'];
        if (!list) {
            return null;
        }
        const flatList = user.listStore.getList({ namespace: 'chatList' });
        let options = {};
        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
            ...options
        };
        return (
            <div className={styles.chatListOuterBox} >
                <div className={styles.chatListBox}>
                    {flatList.map((item, index) => {
                        const peerUser = this.getChatPeer(item);
                        return <div key={index} className={classNames(styles.chatItem, { [styles.itemActive]: item.roomId === this.props.selectedRoomId })}>
                            <Avatar
                                avatarUrl={peerUser?.avatar}
                                userCode={peerUser?.code}
                                size={44}
                                titleMark={peerUser?.titleMark}
                                level={peerUser?.level}
                                officialSize={12}
                                levelBadgeWidth={18}
                                levelBadgeHeight={12}
                            ></Avatar>
                            <div className={styles.info} onClick={() => this.props.onSelectChatRoom?.(item.roomId)}>
                                <div className={styles.infoTop}>
                                    <div className={styles.nickName}>{peerUser?.nickname}</div>
                                    <div className={styles.time}>{formatTime(item.messageAt, false)}</div>
                                </div>
                                <div className={styles.infoBottom}>
                                    <div className={styles.content}>{parse(this.replaceEmojiInContent(item.lastMessage?.content, true), parserOptions)}</div>
                                    {item.unreadCount > 0 && <div className={styles.num}>{item.unreadCount > 99 ? '99+' : item.unreadCount}</div>}
                                </div>
                            </div>
                        </div>
                    })}
                </div>
            </div>
        )
    }

    // 私信聊天记录
    renderUserChatDetail = () => {
        const { user, userChatList } = this.props;
        if (!user || !userChatList) {
            return null;
        }
        let options = {};
        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
            ...options
        };
        return (
            <div className={styles.chatDetailOuterBox}>
                <div className={styles.chatDetailBox} ref={this.chatBoxRef} onScroll={this.handleChatScroll}>
                    {userChatList.map((item, index) => {
                        const isMyMessage = item.sender?.code === user.userInfo?.code;
                        const isImageMessage = item.imgUrl && item.type === 2;
                        // 渲染消息内容
                        const renderMessageContent = () => {
                            if (isImageMessage) {
                                return (
                                    <Image
                                        className={styles.messageImg}
                                        src={item.imgUrl}
                                        alt="message"
                                        preview={{ mask: '预览' }}
                                    />
                                );
                            }

                            return (
                                <div className={styles.messageContentBox}>
                                    <div className={styles.content}>{parse(this.replaceEmojiInContent(item.content, true), parserOptions)}</div>
                                </div>
                            );
                        };

                        if (isMyMessage) {
                            return (
                                <div key={index}>
                                    {item.timestamp && <div className={styles.timestamp}>{item.timestamp}</div>}
                                    <div className={styles.privateRightBox}>
                                        <div className={styles.privateContent}>{renderMessageContent()}</div>
                                        <Avatar
                                            avatarUrl={item.sender?.avatar}
                                            userCode={item.sender?.code}
                                            size={40}
                                            showOfficialTag={false}
                                        ></Avatar>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={index}>
                                {item.timestamp && <div className={styles.timestamp}>{item.timestamp}</div>}
                                <div className={styles.privateLeftBox}>
                                    <Avatar
                                        avatarUrl={item.sender?.avatar}
                                        userCode={item.sender?.code}
                                        size={40}
                                        showOfficialTag={false}
                                    ></Avatar>
                                    <div className={styles.privateContent}>{renderMessageContent()}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    render() {
        const { user, selectedRoomId, chatEmojiPickerVisible, privateUserInfo, unreadNotificationsData } = this.props;
        const flatList = user?.listStore?.getList({ namespace: 'chatList' }) || [];

        // 根据 selectedRoomId 查找当前聊天对象信息
        const selectedChat = selectedRoomId
            ? flatList.find((item: any) => item.roomId === selectedRoomId)
            : null;
        // 从路由读取 usercode 参数
        const routerParams = new URLSearchParams(this.props.location.search);
        const currentUsercode = routerParams?.get('usercode') || '';

        // 当前私信对象信息：优先 selectedChat.receiver，其次 privateUserInfo
        const currentReceiver = this.getChatPeer(selectedChat) || privateUserInfo;
        return (<div className={styles.wrapper}>
            <div className={styles.privateLeft}>
                <div className={styles.privateTitleBox}>
                    <div className={styles.privateTitle}>我的私信{(!currentUsercode && unreadNotificationsData.dialogNotifications > 0) && <span>({unreadNotificationsData.dialogNotifications})</span>}</div>
                    <img src={`${BASE_IMG_URL}/message/icon4.png`} alt="一键已读" className={styles.readNotifications} onClick={() => this.props.submitNotificationsReadData?.()}/>
                </div>
                {currentUsercode && <div className={styles.privateUserInfo}>
                    <Avatar
                        avatarUrl={privateUserInfo?.avatar || privateUserInfo?.avatarUrl}
                        userCode={privateUserInfo?.code}
                        size={44}
                        titleMark={privateUserInfo?.titleMark}
                        level={privateUserInfo?.level}
                        officialSize={12}
                        levelBadgeWidth={18}
                        levelBadgeHeight={12}
                    ></Avatar>
                    <div className={styles.nickName}>{privateUserInfo?.nickname}</div>
                    <div className={styles.time}>{formatTime()}</div>
                </div>}
                {!currentUsercode && flatList.length > 0 && this.renderChatList()}
                {/* 私信列表为空且时且不是针对某一个人的私信聊天 */}
                {!currentUsercode && flatList.length == 0 && <div className={styles.nullBox}>
                    <img src={`${BASE_IMG_URL}/common/null.png`} alt="" className={styles.nullIcon} />
                    <div className={styles.nullText}>这里是空的~</div>
                </div>}
            </div>
            <div className={styles.privateRight}>
                {(currentUsercode || flatList.length > 0) && <><div className={styles.senderUserDetail}>
                    {currentReceiver ? (
                        <>
                            <div className={styles.senderNickname}>{currentReceiver.nickname}</div>
                        </>
                    ) : null}
                </div>
                <div className={styles.privateChatContent}>
                    {this.renderUserChatDetail()}
                </div>
                <div className={styles.privateSendBox}>
                    <div className={styles.operaIconBox}>
                        {chatEmojiPickerVisible ? <img
                            src={`${BASE_IMG_URL}/message/icon10.png`}
                            alt="添加表情"
                            className={styles.icon}
                            onClick={this.props.onChatEmojiToggle}
                        /> : <img
                            src={`${BASE_IMG_URL}/message/icon8.png`}
                            alt="添加表情"
                            className={styles.icon}
                            onClick={this.props.onChatEmojiToggle}
                        />}
                        <div className={styles.uploadBox}>
                            <input
                                ref={this.fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={this.handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <img
                                src={`${BASE_IMG_URL}/message/icon9.png`}
                                alt="上传图片"
                                className={styles.icon}
                                onClick={() => this.fileInputRef.current?.click()}
                            />
                        </div>
                    </div>
                    <Spin spinning={this.props.chatUploading || this.props.chatSendLoading || false}>
                        <textarea
                            ref={this.textareaRef}
                            placeholder='请输入内容'
                            className={styles.privateInput}
                            value={this.props.chatInputContent || ''}
                            onChange={this.handleInputChange}
                        />
                    </Spin>
                    <div className={styles.sendBtnBox}>
                        <div
                            className={classNames(styles.sendBtn, { [styles.sendBtnDisable]: !(this.props.chatInputContent || '').trim() })}
                            onClick={this.props.onChatSend}
                        >发送</div>
                    </div>
                    {/* 表情选择器 */}
                    {this.props.chatEmojiPickerVisible && (
                        <EmojiPicker
                            visible={this.props.chatEmojiPickerVisible}
                            onClose={this.props.onChatEmojiClose || (() => {})}
                            onSelect={this.handleEmojiSelect}
                            position={{ top: -290, left: 12 }}
                            showEmojiTab
                        />
                    )}
                </div></>}
            </div>
        </div>)

    }
}

export default withRouter(PrivateMessagePCPage);
